/**
 * Pure resolver for the dashboard "Grade and roles" block.
 *
 * The block visualises competency coverage as a radar + a progress ring. Which
 * grade those are computed from depends on where the user sits on the ladder:
 *
 *  - `toNextGrade` — there is a higher grade → show coverage of the NEXT grade
 *    and progress toward it (the original behaviour).
 *  - `topGrade` — the user is already at the highest grade in their map → there
 *    is no "next", so show coverage/mastery of the CURRENT grade instead of an
 *    empty radar and a 0% ring.
 *  - `noData` — no map for the profession, the grade is missing from the map, or
 *    the grade has no competencies → nothing to chart; the caller shows a
 *    compact grade label instead.
 *
 * Kept free of React and i18n so it can be unit-tested in isolation; the caller
 * maps `coverage` into chart data and supplies translated labels.
 */

export type GradeDisplayMode = 'toNextGrade' | 'topGrade' | 'noData';

interface CompetencyRef {
  _id: string;
  swimlaneId?: string;
}

interface GradeNode {
  _id: string;
  title?: string;
  competencies?: CompetencyRef[];
}

interface SwimlaneRef {
  _id: string;
  title?: string;
}

export interface CompetencyMapLike {
  grades?: GradeNode[];
  swimlanes?: SwimlaneRef[];
}

export interface SwimlaneCoverage {
  swimlane: string;
  acquired: number;
  total: number;
  pct: number;
}

export interface GradeDisplay {
  mode: GradeDisplayMode;
  currentGrade?: GradeNode;
  nextGrade?: GradeNode;
  /** Grade whose competencies drive the radar/ring: nextGrade ?? currentGrade. */
  displayGrade?: GradeNode;
  /** 0–100. Progress to next grade (toNextGrade) or current-grade mastery (topGrade). */
  progressPct: number;
  acquiredCount: number;
  totalCount: number;
  coverage: SwimlaneCoverage[];
}

export function resolveGradeDisplay(
  map: CompetencyMapLike | null | undefined,
  acquiredCompetencyIds: Iterable<string>,
  currentGradeId: string | null | undefined,
): GradeDisplay {
  const empty: GradeDisplay = {
    mode: 'noData',
    progressPct: 0,
    acquiredCount: 0,
    totalCount: 0,
    coverage: [],
  };

  const grades = map?.grades;
  if (!grades?.length || !currentGradeId) return empty;

  const idx = grades.findIndex((g) => g._id === currentGradeId);
  if (idx === -1) return empty;

  const currentGrade = grades[idx];
  const nextGrade = grades[idx + 1];

  let mode: GradeDisplayMode;
  if (nextGrade) {
    mode = 'toNextGrade';
  } else if ((currentGrade.competencies?.length ?? 0) > 0) {
    mode = 'topGrade';
  } else {
    // Highest grade but no competencies to chart — surface the grade only.
    return { ...empty, currentGrade, displayGrade: currentGrade };
  }

  const displayGrade = nextGrade ?? currentGrade;
  const acquired =
    acquiredCompetencyIds instanceof Set
      ? (acquiredCompetencyIds as Set<string>)
      : new Set(acquiredCompetencyIds);

  const swimlaneTitle = new Map((map?.swimlanes ?? []).map((s) => [s._id, s.title ?? s._id]));

  const byLane = new Map<string, { title: string; total: number; acquired: number }>();
  let acquiredCount = 0;
  for (const comp of displayGrade.competencies ?? []) {
    const laneId = comp.swimlaneId ?? '—';
    const title = swimlaneTitle.get(laneId) ?? laneId;
    let stat = byLane.get(laneId);
    if (!stat) {
      stat = { title, total: 0, acquired: 0 };
      byLane.set(laneId, stat);
    }
    stat.total += 1;
    if (acquired.has(comp._id)) {
      stat.acquired += 1;
      acquiredCount += 1;
    }
  }

  const coverage: SwimlaneCoverage[] = Array.from(byLane.values()).map((s) => ({
    swimlane: s.title,
    acquired: s.acquired,
    total: s.total,
    pct: s.total > 0 ? Math.round((s.acquired / s.total) * 100) : 0,
  }));

  const totalCount = displayGrade.competencies?.length ?? 0;
  const progressPct = totalCount > 0 ? Math.round((acquiredCount / totalCount) * 100) : 0;

  return {
    mode,
    currentGrade,
    nextGrade,
    displayGrade,
    progressPct,
    acquiredCount,
    totalCount,
    coverage,
  };
}
